import { KARAOKE_TRACK_NUMBER } from '@src/editor/constants';
import {
  useEditor,
  useKaraokeStore,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import { analytics } from '@src/utility/analytics';
import { inDebugMode } from '@src/utility/debug';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';

import ClockIcon from '../../../svgs/ClockIcon';
import CopyIcon from '../../../svgs/CopyIcon';
import DeleteIcon from '../../../svgs/DeleteIcon';
import { DocumentLinesIcon } from '../../../svgs/DocumentLinesIcon';
import DownloadIcon from '../../../svgs/DownloadIcon';
import RefreshIcon from '../../../svgs/RefreshIcon';
import SpeakersIcon from '../../../svgs/SpeakersIcon';
import { RepairIcon } from '../../common/icons';
import { type TranscriptOverflowItem } from '../TranscriptOverflowMenu';
import { useTranscriptCopy } from '../useTranscriptCopy';

import type { ITranscriptVideoCreator } from '@src/stores/ITranscriptVideoCreator';
import type { TranscriptElement } from '@src/types.ts/transcript';

interface Options {
  videoCreator: ITranscriptVideoCreator;
  transcriptElements: TranscriptElement[] | undefined;
  getSpeakerName: (label: string | undefined) => string;
  language: string | null;
  /** Only the persistence call the caption actions need. */
  saveService?: { save(asFinal: boolean): Promise<unknown> };
  showSubtitles: boolean;
  hasOriginalTranscription: boolean;
  hideCaptions: boolean;
  isInKaraokeView: boolean;
  /** Opens the transcript debug modal; only surfaced in debug mode. */
  onOpenDebug: () => void;
}

/**
 * PLAT-10646 — contextual actions for the transcript header's trailing
 * "More actions" kebab. Same gating the standalone repair button and
 * captions gear menu used; hosting them inside one always-anchored menu
 * keeps the toolbar row stable when the captions view toggles.
 *
 * Extracted from `Transcript.tsx` to keep that file under its size ratchet
 * (see `__tests__/Transcript.budget.test.ts`).
 */
export function useTranscriptOverflowItems({
  videoCreator,
  transcriptElements,
  getSpeakerName,
  language,
  saveService,
  showSubtitles,
  hasOriginalTranscription,
  hideCaptions,
  isInKaraokeView,
  onOpenDebug,
}: Options): TranscriptOverflowItem[] {
  const userIdentity = useUserIdentity();
  const { enableFixTranscriptButton, enableTranslationReviewerMode } =
    useFlagsCombination(userIdentity.currentRole);
  const editorStore = useEditor();
  const karaokeStore = useKaraokeStore();

  const { hasTranscript, copyTranscript, downloadSrt } = useTranscriptCopy({
    elements: transcriptElements,
    getSpeakerName,
    language,
    analyticsContext: {
      storyId: videoCreator.story?.id,
      storyTitle: videoCreator.story?.title,
      videoId: videoCreator.currentVideo?.id,
      videoTitle: videoCreator.currentVideo?.title,
    },
  });
  const runCopy = (withSpeakers: boolean, withTimecodes: boolean) => {
    copyTranscript(withSpeakers, withTimecodes);
    videoCreator.showToast({ state: 'success', message: 'Transcript copied' });
  };

  const handleResetCaptions = async () => {
    const editor = editorStore.editor;
    if (!editor) return;
    // Drop all synthetic-break records via the command path (reversible,
    // fires markDirty) so the re-produce starts from the producer's
    // default segmentation, not from the post-split state.
    editor.commandClearSyntheticCaptionBreaks();
    editor.commandProduceKaraoke({ replaceTrack: true });
    // Persist the regenerated caption track so the reset survives a
    // refresh. The command updates the timeline but doesn't itself
    // trigger a story save.
    await saveService?.save(false);
  };

  const handleClearCaptions = async () => {
    const editor = editorStore.editor;
    if (!editor) return;
    // Literally delete the captions track via the proper command path.
    // `commandRemoveTrack` wraps in `executeInBatch`, fires TRACK_REMOVED
    // + TIMELINE_CHANGED so all observers (KaraokeStore,
    // ClipPreviewEditorStore, renderer sync) pick up the removal AND
    // captures undo state, so the deletion lands durably and reverts
    // cleanly. Mirrors how the rest of the codebase removes tracks.
    editor.commandRemoveTrack(String(KARAOKE_TRACK_NUMBER));
    // Hide subtitles too so the now-removed track doesn't leave a stale
    // captions overlay.
    videoCreator.setShowSubtitles(false);
    // Persist the cleared captions track so the removal survives a
    // refresh.
    await saveService?.save(false);
  };

  // PLAT-11035 — a translation reviewer gets no transcript export or repair
  // actions. Returning an empty list is all it takes: the menu renders
  // nothing when it has no items, so no host has to pass a hide flag.
  if (enableTranslationReviewerMode) return [];

  const overflowItems: TranscriptOverflowItem[] = [];
  if (hasTranscript) {
    overflowItems.push(
      {
        key: 'copy-plain',
        label: 'Copy Transcript',
        icon: <CopyIcon strokeColor="currentColor" />,
        testId: 'transcript-copy-plain',
        onClick: () => runCopy(false, false),
      },
      {
        key: 'copy-speakers',
        label: 'Copy with Speakers',
        icon: <SpeakersIcon strokeColor="currentColor" />,
        testId: 'transcript-copy-speakers',
        onClick: () => runCopy(true, false),
      },
      {
        key: 'copy-timecodes',
        label: 'Copy with Timecodes',
        icon: <ClockIcon strokeColor="currentColor" />,
        testId: 'transcript-copy-timecodes',
        onClick: () => runCopy(false, true),
      },
      {
        key: 'copy-speakers-timecodes',
        label: 'Copy with Speakers and Timecodes',
        icon: <DocumentLinesIcon />,
        testId: 'transcript-copy-speakers-timecodes',
        onClick: () => runCopy(true, true),
      },
      {
        key: 'download-srt',
        label: 'Download SRT',
        icon: <DownloadIcon strokeColor="currentColor" />,
        testId: 'transcript-download-srt',
        onClick: downloadSrt,
      },
    );
  }
  if (enableFixTranscriptButton && !showSubtitles) {
    overflowItems.push({
      key: 'fix-transcript',
      label: 'Fix Transcript Alignment',
      icon: <RepairIcon fillColor="currentColor" />,
      testId: 'transcript-fix-alignment-button',
      onClick: () => {
        analytics.track('fix_transcription', {
          storyId: videoCreator.story?.id,
          storyTitle: videoCreator.story?.title,
          videoId: videoCreator.currentVideo?.id,
          videoTitle: videoCreator.currentVideo?.title,
        });
        editorStore.restoreTranscriptFromOriginal();
      },
    });
  }
  if (
    hasOriginalTranscription &&
    !hideCaptions &&
    karaokeStore.hasKaraokeLines &&
    isInKaraokeView
  ) {
    overflowItems.push({
      key: 'reset-captions',
      label: 'Reset Captions',
      icon: <RefreshIcon strokeColor="currentColor" />,
      testId: 'captions-reset-button',
      onClick: () => void handleResetCaptions(),
    });
    overflowItems.push({
      key: 'clear-captions',
      label: 'Clear Captions',
      icon: <DeleteIcon />,
      danger: true,
      testId: 'captions-clear-button',
      onClick: () => void handleClearCaptions(),
    });
  }
  // PLAT-11039: opens from this menu, not a header button — the header row has
  // no room for a dev-only control and this menu holds the utility actions.
  if (inDebugMode()) {
    overflowItems.push({
      key: 'transcript-debug',
      label: 'Transcript Debug',
      testId: 'transcript-overflow-debug',
      onClick: onOpenDebug,
    });
  }
  return overflowItems;
}
